/**
 * Discount code block (THEME.md §7.9, T-159): the element of `snippets/okt-discount-code.liquid` in the
 * Discount code block.
 *
 * - Follows the variant: on Horizon's product select event it takes its own block from the section HTML that
 *   Horizon fetched for the new variant, so the code and the price after it match the chosen size; it hides when
 *   that HTML has no code (no Discount code offer applies).
 * - The Button applies the code and adds the chosen variant: it keeps the codes already in the cart and adds its own
 *   (`discount` on the cart update route, as Horizon's cart discount field sends it), then submits Horizon's product
 *   form in the same section (`requestSubmit`, so Horizon's own add to cart runs: its checks, the cart drawer and
 *   the drawer's Discount row, which it opens once the code shows there). Live posted the code alone, which dropped
 *   other codes, then clicked add to cart.
 */
import { StandardEvents } from '@shopify/events';
import { morph } from '@theme/morph';
import { fetchConfig } from '@theme/utilities';

class OktDiscountCode extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('click', this.#handleClick, { signal });
    this.#scope?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  /** @returns {Element | null} */
  get #scope() {
    return this.closest('.shopify-section, dialog');
  }

  /** @param {Event & { promise: Promise<any> }} event */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const next = detail.html.querySelector(`okt-discount-code[data-block-id="${this.dataset.blockId}"]`);
        if (next) {
          morph(this, next);
        } else {
          this.hidden = true;
          this.replaceChildren();
        }
      })
      .catch((/** @type {any} */ error) => {
        if (error?.name !== 'AbortError') console.warn('[okt-discount-code] Event promise rejected:', error);
      });
  };

  /** @param {MouseEvent} event */
  #handleClick = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-okt-apply]') : null;
    const code = this.dataset.code;
    if (!(button instanceof HTMLButtonElement) || button.disabled || !code) return;
    if (button.getAttribute('aria-busy') === 'true') return;

    button.setAttribute('aria-busy', 'true');

    try {
      const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then(
        (response) => response.json()
      );
      /** @type {string[]} */
      const codes = (cart.discount_codes || []).map((/** @type {{ code: string }} */ discount) => discount.code);

      if (!codes.some((existing) => existing.toUpperCase() === code.toUpperCase())) {
        await fetch(
          Theme.routes.cart_update_url,
          fetchConfig('json', { body: JSON.stringify({ discount: [...codes, code].join(',') }) })
        );
      }
    } catch (error) {
      console.warn('[okt-discount-code] Applying the code failed:', error);
    } finally {
      button.removeAttribute('aria-busy');
    }

    this.#openDiscountRow(code);

    const form = this.#scope?.querySelector('product-form-component form[data-type="add-to-cart-form"]');
    if (form instanceof HTMLFormElement) form.requestSubmit();
  };

  /**
   * Opens the cart's Discount row once it holds the code, so the drawer shows the code after the add. Horizon's
   * accordion closes the row whenever it is drawn, and its re-render keeps the row's open state.
   * @param {string} code
   */
  #openDiscountRow(code) {
    const open = () => {
      const pill = document.querySelector(`cart-discount-component [data-discount-code="${CSS.escape(code)}"]`);
      const details = pill?.closest('details');
      if (details) details.open = true;
      return Boolean(details);
    };
    if (open()) return;

    const observer = new MutationObserver(() => {
      if (open()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 6000);
  }
}

if (!customElements.get('okt-discount-code')) {
  customElements.define('okt-discount-code', OktDiscountCode);
}
